import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LoadingButton from '@tracker/shared/src/LoadingIndicator/LoadingButton';
import {makeRequired, makeValidate, Switches, TextField} from 'mui-rff';
import React, {useCallback, useMemo} from 'react';
import {Form} from 'react-final-form';
import * as Yup from 'yup';
import {BookmarkLinkWithId, linkIdGenerator} from './Home';

type Props = {
    open : boolean;
    link ?: BookmarkLinkWithId;
    onSubmit : (link : BookmarkLinkWithId) => Promise<void>;
    onClose : () => void;
};

const schema = Yup.object().shape({
    title: Yup.string().label('Title').required().trim(),
    url: Yup.string().label('URL').url().required(),
    openInNew: Yup.boolean().notRequired().default(false),
    favorite: Yup.boolean().notRequired().default(false),
}).required();

type FormData = Yup.InferType<typeof schema>;

const validate = makeValidate(schema);
const required = makeRequired(schema);

const LinkFormDialog : React.FC<Props> = ({open, link, onSubmit, onClose} : Props) => {
    const handleSubmit = useCallback(async (values : FormData) => {
        const data = schema.cast(values);
        await onSubmit({
            id: link ? link.id : linkIdGenerator(),
            ...data,
        });
        onClose();
    }, [onSubmit, onClose, link]);

    const initialValues = useMemo(() => link ? link : undefined, [link]);

    return (
        <Form
            subscription={{submitting: true, pristine: true}}
            onSubmit={handleSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({handleSubmit, pristine, submitting, form: {reset}}) => (
                <Dialog
                    open={open}
                    onExited={() => reset()}
                    disableEscapeKeyDown={submitting}
                    onClose={(_event, reason) => {
                        if(reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                            onClose()
                        }
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        component: 'form',
                        onSubmit: handleSubmit,
                        // @ts-ignore Workaround for https://github.com/mui-org/material-ui/issues/13253
                        noValidate: true,
                    }}
                >
                    <DialogTitle>{link ? 'Edit' : 'Create'} Link</DialogTitle>
                    <DialogContent>
                        <TextField
                            variant="outlined"
                            label="URL"
                            name="url"
                            required={required.url}
                            autoFocus
                        />
                        <TextField
                            variant="outlined"
                            label="Title"
                            name="title"
                            required={required.title}
                        />
                        <Switches
                            name="openInNew"
                            color="primary"
                            data={{value: true, label: 'Open in new tab'}}
                        />
                        <Switches
                            name="favorite"
                            color="primary"
                            data={{value: true, label: 'Favorite (Displays in user menu)'}}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => onClose()} disabled={submitting}>Cancel</Button>
                        <LoadingButton
                            loading={submitting}
                            type="submit"
                            color="primary"
                            disabled={pristine || submitting}
                        >
                            {link ? 'Update' : 'Create'}
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            )}
        />
    );
};

export default LinkFormDialog;
